<script lang="ts">
  import { Icon } from '@shared/ui/icon';
  import type { IconNames, Variants } from '@shared/ui/icon';
  import { Loader } from '@shared/ui/loader';
  import { is } from '@shared/utils';

  // standard icon size = 20x20

  export let variant: 'primary' | 'secondary' | 'tertiary' | 'quaternary' = 'primary';
  export let size: 'sm' | 'md' | 'lg' = 'md';
  export let hugged = false;

  export let loading = false;
  export let disabled = false;

  export let type: 'button' | 'submit' = 'button';
  export let href: string | undefined = undefined;
  export let target = '';
  export let rel: string | undefined = undefined;

  export let iconName: IconNames | undefined = undefined;
  export let iconVariant: keyof Variants = 'default';
  export let iconPosition: 'left' | 'right' = 'right';

  // const iconSize = 20 // can be moved to props
  export let iconWidth: number | undefined = undefined;
  export let iconHeight: number | undefined = undefined;
  export let htmlElement: HTMLButtonElement | HTMLAnchorElement | undefined = undefined;
</script>

<svelte:element
  this="{href ? 'a' : 'button'}"
  bind:this="{htmlElement}"
  rel="{rel ? rel : undefined}"
  on:click
  role="button"
  tabindex="0"
  href="{href ? href : undefined}"
  {target}
  disabled="{loading || disabled}"
  {type}
  class="box-border flex items-center justify-center rounded-lg transition-[background,transform,color,border] active:scale-95
	{is(hugged, 'w-fit', 'w-full')}
	{is(
    variant === 'primary',
    // default and hover states
    'border-default-900 bg-default-900 text-default-50 border ' +
      'enabled:hover:border-default-800 enabled:hover:bg-default-800 ' +
      'dark:text-dark-50 dark:hover:border-dark-200 dark:hover:bg-dark-400 dark:bg-white dark:hover:text-white ' +
      is(
        loading,
        // loading state
        ' disabled:bg-default-700 dark:disabled:bg-dark-100 disabled:text-white dark:disabled:text-white ',
        // disabled state
        ' disabled:border-default-200 disabled:bg-default-200 disabled:text-default-400 dark:disabled:border-dark-300 dark:disabled:bg-dark-300 dark:disabled:text-dark-350',
      ),
  )}
	{is(
    variant === 'secondary',
    // default and hover states
    'enabled:border-default-300 border ' +
      'enabled:text-default-900 bg-transparent ' +
      'enabled:hover:border-default-900 enabled:hover:bg-default-900 enabled:hover:text-default-50 ' +
      'dark:enabled:border-dark-400 dark:enabled:hover:border-default-800 dark:enabled:hover:bg-default-800 dark:enabled:text-white ' +
      is(
        loading,
        // loading state
        ' disabled:border-default-300 dark:disabled:border-dark-400 disabled:text-black dark:disabled:text-white ',
        // disabled state
        ' disabled:border-default-400 disabled:text-default-400 dark:disabled:border-dark-200 dark:disabled:text-dark-600',
      ),
  )}
	{is(
    variant === 'tertiary',
    // default and hover states
    'border-default-200 border ' +
      'bg-default-200 text-default-900 ' +
      'enabled:hover:border-default-900 enabled:hover:bg-default-900 enabled:hover:text-default-50 ' +
      'dark:border-dark-350 dark:bg-dark-350 dark:text-white dark:enabled:hover:border-white dark:enabled:hover:bg-white dark:enabled:hover:text-black  ' +
      is(
        loading,
        // loading state
        ' dark:disabled:border-default-900 dark:disabled:bg-default-900 dark:disabled:text-white ',
        // disabled state
        ' disabled:border-default-200 disabled:bg-default-200 disabled:text-default-500 dark:disabled:border-dark-300 dark:disabled:bg-dark-300 dark:disabled:text-dark-350',
      ),
  )}
  {is(
    variant === 'quaternary',
    // default and hover states
    'border-4 border-black/0 backdrop-blur-xl ' +
      'bg-black/15 text-white ' +
      'enabled:hover:bg-white enabled:hover:text-black ' +
      'dark:bg-default-600/35 dark:text-white dark:enabled:hover:bg-white dark:enabled:hover:text-black  ' +
      is(
        loading,
        // loading state
        ' dark:disabled:border-default-900 dark:disabled:bg-default-900 dark:disabled:text-white ',
        // disabled state
        ' disabled:border-default-200 disabled:bg-default-200 disabled:text-default-500 dark:disabled:border-dark-300 dark:disabled:bg-dark-300 dark:disabled:text-dark-350',
      ),
  )}
  {is(size === 'sm', 'gap-0.5 px-3 py-2 text-sm font-medium')}
	{is(size === 'md', 'gap-1 px-3.5 py-3 text-base font-medium')}
	{is(size === 'lg', 'gap-1 px-4 py-3 text-lg font-medium')}
	{$$props.class}"
>
  {#if $$slots['default']}
    <div class="px-1.5 {is(iconPosition === 'left', 'order-2')}">
      <slot />
    </div>
  {/if}

  {#if iconName || loading}
    <!-- height is set for case 'button with icon without text' -->
    <div class="flex items-center justify-center {size === 'sm' ? 'h-5' : size === 'md' ? 'h-6' : 'h-7'}">
      {#if iconName}
        <Icon name="{iconName}" variant="{iconVariant}" width="{iconWidth}" height="{iconHeight}" />
      {:else if loading}
        <Loader classes="shrink-0" />
      {/if}
    </div>
  {/if}
</svelte:element>
